import React from "react";

import { midTrunc, shortDay, usdShort } from "./util";

export { Delta } from "../../design/Delta";
export { Sparkline } from "../../design/Sparkline";
export { InfoDot } from "../../design/Tooltip";
export { Segmented } from "../../design/Tabs";
export { Select } from "../../design/Field";

/** Ranked rows: label, a neutral bar scaled to the top row, value. */
export function BarList({
  rows,
  format = (v) => v.toLocaleString("en-US"),
  onRow,
  color = "var(--ds-bar)",
}: {
  rows: { key: string; label: React.ReactNode; value: number }[];
  format?: (v: number) => string;
  /** Makes each row a button, e.g. to open that entity's details. */
  onRow?: (key: string, label: string) => void;
  color?: string;
}) {
  const ranked = rows.filter((r) => r.key !== "__other__");
  const max = Math.max(
    1e-9,
    ...(ranked.length ? ranked : rows).map((r) => r.value),
  );
  return (
    <div className="ds-barlist">
      {rows.map((r) => {
        const text = typeof r.label === "string" ? r.label : r.key;
        const folded = r.key === "__other__";
        const inner = (
          <>
            <span className="ds-barlist-label" title={text}>
              {typeof r.label === "string" ? midTrunc(r.label) : r.label}
            </span>
            {folded ? (
              <span aria-hidden />
            ) : (
              <span className="ds-barlist-track">
                <span
                  style={{
                    width: `${Math.max(1, (r.value / max) * 100)}%`,
                    background: color,
                  }}
                />
              </span>
            )}
            <span className="ds-barlist-value">{format(r.value)}</span>
            {onRow &&
              (folded ? (
                <span aria-hidden />
              ) : (
                <span className="ds-list-chev" aria-hidden />
              ))}
          </>
        );
        return onRow && !folded ? (
          <button
            key={r.key}
            type="button"
            className="ds-barlist-row click"
            onClick={() => onRow(r.key, text)}
          >
            {inner}
          </button>
        ) : (
          <div
            key={r.key}
            className={`ds-barlist-row${folded ? " folded" : ""}${onRow ? " spaced" : ""}`}
          >
            {inner}
          </div>
        );
      })}
    </div>
  );
}

export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const exp = 10 ** Math.floor(Math.log10(v));
  // Steps whose quarters are round numbers (3 → 0.75 would print as 2.3K).
  const step = [1, 1.2, 1.6, 2, 2.4, 3.2, 4, 4.8, 6, 8, 10].find(
    (f) => f * exp >= v,
  );
  return (step ?? 10) * exp;
}

export type StackDay = { day: string; values: Record<string, number> };

function isWeekend(day: string): boolean {
  const d = new Date(`${day}T00:00:00Z`).getUTCDay();
  return d === 0 || d === 6;
}

/** Stacked bars or overlaid lines over days, one series per key in ``order``. */
export function TimeChart({
  days,
  order,
  colorOf,
  labelOf,
  format = usdShort,
  kind = "bars",
  compact = false,
  target,
  targetLabel,
  additive = true,
}: {
  days: StackDay[];
  order: string[];
  colorOf: (key: string) => string;
  labelOf: (key: string) => string;
  format?: (v: number) => string;
  kind?: "bars" | "line" | "area";
  compact?: boolean;
  /** Dashed horizontal reference line, in the same units as the values. */
  target?: number;
  targetLabel?: string;
  /** False for rates and shares, which mean nothing summed across days. */
  additive?: boolean;
}) {
  const [hover, setHover] = React.useState<number | null>(null);
  const totals = days.map((d) =>
    order.reduce((s, k) => s + (d.values[k] ?? 0), 0),
  );
  const peak =
    kind === "bars"
      ? Math.max(0, ...totals)
      : Math.max(0, ...days.flatMap((d) => order.map((k) => d.values[k] ?? 0)));
  const top = niceMax(Math.max(peak, (target ?? 0) * 1.15));
  // Rows are rounded alike so they visibly add up to the total.
  const tipFormat =
    format === usdShort
      ? (v: number) => `$${Math.round(v).toLocaleString("en-US")}`
      : format;
  const lined = kind !== "bars";
  const ticks = [1, 0.75, 0.5, 0.25, 0];
  const yTicks = compact ? [1, 0.5, 0] : ticks;
  const tickText = (f: number) => format(top * f).replace(/\.00(?=\D*$)/, "");
  const every = Math.max(1, Math.ceil(days.length / (compact ? 2 : 10)));
  const shade = !compact && days.length > 1 && days.length <= 92;
  const n = Math.max(1, days.length);
  const xPct = (i: number) => ((i + 0.5) / n) * 100;
  const last = days.length - 1;
  const tip = (i: number) => {
    const d = days[i];
    const rows = order
      .filter((k) => (d.values[k] ?? 0) > 0)
      .sort((a, b) => (d.values[b] ?? 0) - (d.values[a] ?? 0));
    const h =
      (kind === "bars"
        ? totals[i]
        : Math.max(...rows.map((k) => d.values[k] ?? 0), 0)) / top;
    const side = n < 3 ? "c" : i < n / 2 ? "l" : "r";
    return (
      <div
        className={`ds-chart-tip ${side}${h > 0.6 ? " low" : ""}`}
        style={h > 0.6 ? undefined : { bottom: `calc(${h * 100}% + 8px)` }}
      >
        <div className="ds-chart-tip-head">{shortDay(d.day)}</div>
        {rows.map((k) => (
          <div key={k} className="ds-chart-tip-row">
            <span className="ds-swatch" style={{ background: colorOf(k) }} />
            {labelOf(k)}
            <strong>{tipFormat(d.values[k] ?? 0)}</strong>
          </div>
        ))}
        {kind === "bars" && rows.length > 1 && (
          <div className="ds-chart-tip-row total">
            Total
            <strong>{tipFormat(totals[i])}</strong>
          </div>
        )}
      </div>
    );
  };
  return (
    <figure
      className={`ds-chart${compact ? " compact" : ""}`}
      tabIndex={days.length ? 0 : undefined}
      onKeyDown={(e) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        const step = e.key === "ArrowRight" ? 1 : -1;
        setHover((h) =>
          Math.min(last, Math.max(0, h == null ? last : h + step)),
        );
      }}
      onBlur={() => setHover(null)}
      role="img"
      aria-label={
        days.length
          ? `${order.map(labelOf).join(", ")} per day, ${shortDay(days[0].day)} to ${shortDay(days[last].day)}: ${additive ? `total ${format(totals.reduce((a, b) => a + b, 0))}` : `latest ${format(totals[last])}`}, peak ${format(peak)}. Arrow keys read each day.`
          : "No data in this range."
      }
    >
      <div className="ds-chart-body">
        {
          <div className="ds-chart-y" aria-hidden>
            {yTicks.map((f, i) => {
              const text = tickText(f);
              // Small integer ranges round two ticks to one label; show it once.
              return i > 0 && text === tickText(yTicks[i - 1]) ? null : (
                <span key={f} style={{ bottom: `${f * 100}%` }}>
                  {text}
                </span>
              );
            })}
          </div>
        }
        <div className="ds-chart-plot">
          <div className="ds-chart-area" onMouseLeave={() => setHover(null)}>
            {yTicks.map((f) => (
              <div
                key={f}
                className={`ds-chart-grid${f === 0 ? " base" : ""}`}
                style={{ bottom: `${f * 100}%` }}
              />
            ))}
            {lined && (
              <svg
                className="ds-chart-svg"
                viewBox={`0 0 ${n} 100`}
                preserveAspectRatio="none"
                aria-hidden
              >
                {order.map((k, si) => {
                  const pts = days
                    .map(
                      (d, i) =>
                        `${i + 0.5},${100 - ((d.values[k] ?? 0) / top) * 100}`,
                    )
                    .join(" ");
                  const gid = `ds-area-${si}-${k.replace(/\W/g, "")}`;
                  return (
                    <g key={k}>
                      {kind === "area" && (
                        <>
                          <defs>
                            <linearGradient
                              id={gid}
                              x1="0"
                              x2="0"
                              y1="0"
                              y2="1"
                            >
                              <stop
                                offset="0"
                                stopColor={colorOf(k)}
                                style={{ stopOpacity: "var(--ds-area-top)" }}
                              />
                              <stop
                                offset="1"
                                stopColor={colorOf(k)}
                                style={{ stopOpacity: "var(--ds-area-bottom)" }}
                              />
                            </linearGradient>
                          </defs>
                          <polygon
                            fill={`url(#${gid})`}
                            points={`0.5,100 ${pts} ${last + 0.5},100`}
                          />
                        </>
                      )}
                      <polyline
                        fill="none"
                        stroke={colorOf(k)}
                        strokeWidth="2"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                        points={pts}
                      />
                    </g>
                  );
                })}
              </svg>
            )}
            {lined &&
              days.length > 0 &&
              order.map((k) => (
                <span
                  key={k}
                  className="ds-chart-end"
                  style={{
                    left: `${xPct(last)}%`,
                    bottom: `${((days[last].values[k] ?? 0) / top) * 100}%`,
                    background: colorOf(k),
                  }}
                />
              ))}
            {target != null && (
              <div
                className="ds-chart-target"
                style={{ bottom: `${(target / top) * 100}%` }}
              >
                {!compact && (
                  <span>{targetLabel ?? `Target ${format(target)}`}</span>
                )}
              </div>
            )}
            <div className="ds-chart-cols">
              {days.map((d, i) => (
                <div
                  key={d.day}
                  className={`ds-chart-col${shade && isWeekend(d.day) ? " wkend" : ""}${hover === i ? " hover" : ""}`}
                  onMouseEnter={() => setHover(i)}
                >
                  {kind === "bars" &&
                    order
                      .filter((k) => (d.values[k] ?? 0) > 0)
                      .map((k) => (
                        <div
                          key={k}
                          className="ds-chart-bar"
                          style={{
                            height: `${((d.values[k] ?? 0) / top) * 100}%`,
                            background: colorOf(k),
                          }}
                        />
                      ))}
                  {hover === i && (totals[i] > 0 || lined) && tip(i)}
                </div>
              ))}
            </div>
          </div>
          <div className="ds-chart-x" aria-hidden>
            {compact
              ? days.length > 0 && (
                  <>
                    <span>{shortDay(days[0].day)}</span>
                    <span>{shortDay(days[last].day)}</span>
                  </>
                )
              : days.map((d, i) => (
                  <span
                    key={d.day}
                    className={
                      i % every === 0 && (i / every) % 2 ? "alt" : undefined
                    }
                  >
                    {i % every === 0 ? shortDay(d.day) : ""}
                  </span>
                ))}
          </div>
        </div>
      </div>
      {compact && target != null && (
        <figcaption className="ds-chart-key">
          <span className="ds-chart-key-dash" />
          {targetLabel ?? `Target ${format(target)}`}
        </figcaption>
      )}
    </figure>
  );
}

export function Legend({
  keys,
  colorOf,
  labelOf,
  single = false,
}: {
  keys: string[];
  colorOf: (k: string) => string;
  labelOf: (k: string) => string;
  /** Show a one-series key too, so sibling charts keep matching footers. */
  single?: boolean;
}) {
  if (keys.length < (single ? 1 : 2)) return null;
  return (
    <div className="ds-legend">
      {keys.map((k) => (
        <span key={k}>
          <span className="ds-swatch" style={{ background: colorOf(k) }} />
          {labelOf(k)}
        </span>
      ))}
    </div>
  );
}

/** Horizontal bars with right-aligned labels beside them, values optional. */
export function HBars({
  rows,
  color = "var(--ds-bar)",
  onRow,
  format,
}: {
  rows: { key: string; label: string; value: number }[];
  color?: string;
  onRow?: (key: string, label: string) => void;
  format?: (v: number) => string;
}) {
  const max = Math.max(1e-9, ...rows.map((r) => r.value));
  return (
    <div className="ds-hbars">
      {rows.map((r) => (
        <div
          key={r.key}
          className={`ds-hbar${onRow ? " click" : ""}${format ? " valued" : ""}`}
          role={onRow ? "button" : undefined}
          tabIndex={onRow ? 0 : undefined}
          onClick={onRow && (() => onRow(r.key, r.label))}
          onKeyDown={
            onRow && ((e) => e.key === "Enter" && onRow(r.key, r.label))
          }
        >
          <span className="ds-hbar-label" title={r.label}>
            {midTrunc(r.label, 20)}
          </span>
          <span className="ds-hbar-track">
            <span
              style={{
                width: `${Math.max(1, (r.value / max) * 100)}%`,
                background: color,
              }}
            />
          </span>
          {format && <span className="ds-hbar-value">{format(r.value)}</span>}
          {onRow && <span className="ds-list-chev" aria-hidden />}
        </div>
      ))}
    </div>
  );
}
